import { useEffect, useState } from "react";
import { useOrganizationCode } from "./extractOrganizationCode";
import { getMyOrganization } from "../api/services/organization";

export enum EOrganizationState {
  LOADING = "LOADING",
  NO_ORGANIZATION = "NO_ORGANIZATION",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  VERIFIED = "VERIFIED",
}

export interface IOrganizationState {
  state: EOrganizationState;
  code?: string;
  rejectionReason?: string;
  name?: string;
  logoUrl?: string;
  websiteUrl?: string;
  description?: string;
}

/**
 * Resolves the logged-in account's organization state for portal screen routing.
 *
 * Claims-first: if the JWT already carries organizationAdminCode/clientAdminClientId (resolved via
 * useOrganizationCode), the org is VERIFIED — no API call needed. Those claims are only ever set at
 * admin-approval time, so their presence is a reliable proxy. Only when they're absent do we call
 * GET /api/organizations/my-organization, since absence alone can't distinguish "no org yet" from
 * PENDING/REJECTED.
 *
 * Known limitation: useOrganizationCode resolves asynchronously, so on first render this hook may
 * briefly call the fallback API before the claim has had a chance to resolve. It self-corrects once
 * useOrganizationCode settles (this hook re-runs off its result), so the only cost is a possible
 * one-frame flicker, not an incorrect final state.
 */
export function useOrganizationState(
  userToken?: string | null
): IOrganizationState {
  const organizationCode = useOrganizationCode(userToken);
  const [state, setState] = useState<IOrganizationState>({
    state: EOrganizationState.LOADING,
  });

  useEffect(() => {
    if (!userToken) {
      return;
    }
    if (organizationCode) {
      setState({ state: EOrganizationState.VERIFIED, code: organizationCode });
      return;
    }

    let cancelled = false;
    getMyOrganization()
      .then((response) => {
        if (cancelled) return;
        const data = response?.data;
        if (!data?.hasOrganization) {
          setState({ state: EOrganizationState.NO_ORGANIZATION });
          return;
        }
        if (data.verificationStatus === "REJECTED") {
          setState({
            state: EOrganizationState.REJECTED,
            code: data.code,
            rejectionReason: data.rejectionReason,
            name: data.name,
            logoUrl: data.logoUrl,
            websiteUrl: data.websiteUrl,
            description: data.description,
          });
        } else if (data.verificationStatus === "VERIFIED") {
          setState({ state: EOrganizationState.VERIFIED, code: data.code });
        } else {
          setState({ state: EOrganizationState.PENDING, code: data.code });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ state: EOrganizationState.NO_ORGANIZATION });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userToken, organizationCode]);

  return state;
}
